import React, { useMemo, useState } from "react";

import {
	Badge,
	Button,
	InfoMessage,
	Spinner,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { Link } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { adminKeys } from "@/constants/CQueryKeys";

import { TAdminReconcileResult, TAdminStripeEvent } from "@/types/TAdmin";

import { promiseToast } from "@/modules/index";

const LIMITS = [50, 200] as const;

/**
 * The Stripe tab: the webhook log with a replay on the deliveries that
 * failed, and the reconcile button — what to open when someone says
 * "I paid and nothing changed".
 */
export const AdminBilling: React.FC = () => {
	const [limit, setLimit] = useState<(typeof LIMITS)[number]>(50);
	const [lastReconcile, setLastReconcile] =
		useState<TAdminReconcileResult | null>(null);
	const queryClient = useQueryClient();

	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.stripeEvents(limit),
		queryFn: () => AdminService.stripeEvents(limit),
		placeholderData: (previous) => previous,
	});

	const invalidate = () => {
		void queryClient.invalidateQueries({ queryKey: adminKeys.all });
	};

	const replay = useMutation({
		mutationFn: (id: string) => AdminService.replayEvent(id),
		onSettled: invalidate,
	});

	const reconcile = useMutation({
		mutationFn: AdminService.reconcileAll,
		onSuccess: (result) => {
			setLastReconcile(result);
			invalidate();
		},
	});

	const runReplay = (id: string) => {
		promiseToast(replay.mutateAsync(id), {
			success: t("admin.billing.replayed"),
		});
	};

	const runReconcile = () => {
		promiseToast(reconcile.mutateAsync(), {
			success: t("admin.billing.reconciled"),
		});
	};

	const columns: TTableColumn<TAdminStripeEvent>[] = useMemo(
		() => [
			{
				key: "receivedAt",
				label: t("admin.billing.columns.received"),
				render: (event) =>
					format(new Date(event.receivedAt), "dd/MM/yyyy HH:mm:ss"),
			},
			{
				key: "type",
				label: t("admin.billing.columns.type"),
				width: "1fr",
				render: (event) => (
					<span className="flex flex-col">
						<span className="font-mono text-sm">{event.type}</span>
						<span className="font-mono text-xs text-neutral-400">
							{event.id}
						</span>
					</span>
				),
			},
			{
				key: "stripeCustomerId",
				label: t("admin.billing.columns.customer"),
				render: (event) =>
					event.stripeCustomerId ? (
						<span className="font-mono text-xs">
							{event.stripeCustomerId}
						</span>
					) : (
						<span className="text-neutral-400">—</span>
					),
			},
			{
				key: "error",
				label: t("admin.billing.columns.status"),
				width: "1fr",
				truncate: false,
				render: (event) =>
					event.error ? (
						<span className="flex flex-col gap-1">
							<Badge color="danger" variant="tint" size="sm">
								{t("admin.billing.status.failed")}
							</Badge>
							<span
								className="text-xs text-neutral-500"
								data-testid="event-error"
							>
								{event.error}
							</span>
						</span>
					) : event.processedAt ? (
						<Badge color="success" variant="tint" size="sm">
							{t("admin.billing.status.processed")}
						</Badge>
					) : (
						<Badge color="neutral" variant="tint" size="sm">
							{t("admin.billing.status.pending")}
						</Badge>
					),
			},
			{
				key: "id",
				label: "",
				width: "120px",
				render: (event) => (
					<Button
						size="sm"
						variant={event.error ? "solid" : "ghost"}
						color={event.error ? "primary" : "neutral"}
						picto="play"
						onClick={() => runReplay(event.id)}
						disabled={replay.isPending}
						aria-label={t("admin.billing.replay")}
						data-testid={`replay-${event.id}`}
					>
						{t("admin.billing.replay")}
					</Button>
				),
			},
		],
		// runReplay is stable enough: it closes over the mutation only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[replay.isPending]
	);

	if (isLoading) return <Spinner />;
	if (error || !data)
		return <InfoMessage color="danger">{t("admin.error")}</InfoMessage>;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<span className="flex items-center gap-2">
					{data.failed > 0 ? (
						<Badge
							color="danger"
							variant="tint"
							data-testid="admin-billing-failed"
						>
							{t("admin.billing.failedCount", {
								count: data.failed,
							})}
						</Badge>
					) : (
						<Badge
							color="success"
							variant="tint"
							data-testid="admin-billing-failed"
						>
							{t("admin.billing.noFailure")}
						</Badge>
					)}
					{LIMITS.map((value) => (
						<Button
							key={value}
							size="sm"
							variant={value === limit ? "solid" : "ghost"}
							color="neutral"
							onClick={() => setLimit(value)}
						>
							{value}
						</Button>
					))}
				</span>
				<Button
					size="sm"
					picto="refresh"
					onClick={runReconcile}
					isLoading={reconcile.isPending}
					data-testid="admin-billing-reconcile"
				>
					{t("admin.billing.reconcile")}
				</Button>
			</div>
			{lastReconcile && (
				<InfoMessage
					color={lastReconcile.failed > 0 ? "warning" : "success"}
					onClose={() => setLastReconcile(null)}
					data-testid="admin-billing-reconcile-result"
				>
					<span className="flex flex-col gap-1">
						<span>
							{t("admin.billing.reconcileResult", {
								reconciled: lastReconcile.reconciled,
								failed: lastReconcile.failed,
							})}
						</span>
						{lastReconcile.failures.map((failure) => (
							<span
								key={failure.subscriptionId}
								className="text-xs"
							>
								<Link
									to={`/admin/users/${failure.userId}`}
									className="underline"
								>
									#{failure.userId}
								</Link>{" "}
								{failure.subscriptionId} — {failure.error}
							</span>
						))}
					</span>
				</InfoMessage>
			)}
			<Table
				items={data.data}
				getItemKey={(event) => event.id}
				columns={columns}
				noDataMessage={t("admin.billing.empty")}
			/>
		</div>
	);
};
